import { AdminService } from 'api/services/admin.service';
import { CommunicationService } from 'api/services/communication.service';
import { ContractorService } from 'api/services/contractor.service';
import { ExportService } from 'api/services/export.service';
import { FileService } from 'api/services/file.service';
import { GeocodeService } from 'api/services/geocode.service';
import { ImportService } from 'api/services/import.service';
import { InspectionService } from 'api/services/inspection.service';
import { InventoryService } from 'api/services/inventory.service';
import { JobService } from 'api/services/job.service';
import { MapService } from 'api/services/map.service';
import { MlService } from 'api/services/ml.service';
import { OptionsService } from 'api/services/options.service';
import { PropertyService } from 'api/services/property.service';
import { PublicService } from 'api/services/public.service';
import { ReplacementService } from 'api/services/replacement.service';
import { SamplesService } from 'api/services/sample.service';
import { ServiceLineService } from 'api/services/serviceline.service';
import FEATURE_FLAGS from 'config/featureFlags';
import useCASTFlags from 'hooks/useCASTFlags';
import React, { createContext } from 'react';

export type ApiServices = {
  adminService: AdminService;
  communicationService: CommunicationService;
  contractorService: ContractorService;
  fileService: FileService;
  geocodeService: GeocodeService;
  importService: ImportService;
  inspectionsService: InspectionService;
  inventoryService: InventoryService;
  exportService: ExportService;
  jobService: JobService;
  mapService: MapService;
  mlService: MlService;
  publicService: PublicService;
  propertyService: PropertyService;
  replacementService: ReplacementService;
  samplesService: SamplesService;
  serviceLineService: ServiceLineService;
};

export type DataCacheServices = {
  optionsService: OptionsService;
};

type AllServices = ApiServices & DataCacheServices;

const ApiServicesContext = createContext<AllServices>(null);

// TODO: add NEXT_PUBLIC to these apiURL env vars so we don't have to pass them all the way down here
const ApiServicesContextProvider = ({
  children,
  apiUrl,
  dataImporterUrl,
  publicApiUrl,
  usersApiUrl,
  arcGISAPIToken,
  token,
  tenantName,
}: {
  children: React.ReactNode;
  apiUrl: string;
  dataImporterUrl: string;
  publicApiUrl: string;
  usersApiUrl: string;
  arcGISAPIToken: string;
  token?: string;
  tenantName: string;
}) => {
  const urls = { apiUrl, dataImporterUrl, publicApiUrl, usersApiUrl };
  const flags = useCASTFlags([FEATURE_FLAGS.LEADCAST_GEOCODING]);
  const forStorage = JSON.parse(flags[FEATURE_FLAGS.LEADCAST_GEOCODING].value as string)
    ?.forStorage;

  const adminService = new AdminService({ serviceUrls: urls, token, tenantName });
  const fileService = new FileService({ serviceUrls: urls, token, tenantName });
  const geocodeService = new GeocodeService({
    apiToken: arcGISAPIToken,
    forStorage,
  });
  const importService = new ImportService({ serviceUrls: urls, token, tenantName });
  const inspectionsService = new InspectionService({ serviceUrls: urls, token, tenantName });
  const inventoryService = new InventoryService({ serviceUrls: urls, token, tenantName });
  const exportService = new ExportService({ serviceUrls: urls, token, tenantName });
  const jobService = new JobService({ serviceUrls: urls, token, tenantName });
  const mapService = new MapService({ serviceUrls: urls, token, tenantName });
  const mlService = new MlService({ serviceUrls: urls, token, tenantName });
  const propertyService = new PropertyService({ serviceUrls: urls, token, tenantName });
  const publicService = new PublicService({ serviceUrls: urls, token, tenantName });
  const replacementService = new ReplacementService({ serviceUrls: urls, token, tenantName });
  const samplesService = new SamplesService({ serviceUrls: urls, token, tenantName });
  const communicationService = new CommunicationService({ serviceUrls: urls, token, tenantName });
  const contractorService = new ContractorService({ serviceUrls: urls, token, tenantName });

  const serviceLineService = new ServiceLineService({ serviceUrls: urls, token, tenantName });

  const apiServices: ApiServices = {
    adminService,
    communicationService,
    contractorService,
    exportService,
    fileService,
    geocodeService,
    importService,
    inspectionsService,
    inventoryService,
    jobService,
    mapService,
    mlService,
    propertyService,
    publicService,
    replacementService,
    samplesService,
    serviceLineService,
  };

  const optionsService = new OptionsService(apiServices);

  return (
    <ApiServicesContext.Provider value={{ ...apiServices, optionsService }}>
      {children}
    </ApiServicesContext.Provider>
  );
};

export { ApiServicesContext, ApiServicesContextProvider };
